import React from "react";
import { STRINGS } from "../../../strings";
import { globalize, translate } from "../../../translate";
import { AppCard, AppCardContent } from "../../Generic/AppCard";
import { AppIcon } from "../../Generic/AppIcon";
import { AppList, AppListItem } from "../../Generic/AppList";
import Scaffold from "../../Scaffold/Scaffold";
import { MINUS, PLUS } from "../../../utils/svgs_new";
import { Earning, Paystub } from "../../../messages/Paystub";
import PaystubSummaryCard from "../components/PaystubSummaryCard";
import AppAccordion from "../../Generic/AppAccordion/AppAccordion";
import { AccordionChild } from "../../Generic/AppAccordion/AppAccordion.types";
import { LocalDate } from "@js-joda/core";
import Flex from "../../Generic/Flex";
import AppAccordionHeader from "../../Generic/AppAccordion/AppAccordionHeader";
import Margin from "../../Margin";
import { formatNumberLocale } from "../../../format";
import ScaffoldTitle from "../../Scaffold/components/ScaffoldTitle";
import styled from "@emotion/styled";
import { DownloadIcon } from "../../../utils/icones";
import Spinner from "../../Generic/Spinner";

interface Props {
  paystub: Paystub;
  isLoading: boolean;
  onClickGoBack: () => void;
  onClickDownload: () => void;
}

const PaystubDetailsScreen = (props: Props) => {
  const { earnings, deductions, taxes, totalGross, totalDeductions, totalTaxes } = props.paystub;

  const earningsHeader = (
    <AppAccordionHeader leading={<AppIcon icon={PLUS} fill="green" />} title={"Earnings"} />
  );
  const taxesHeader = (
    <AppAccordionHeader leading={<AppIcon icon={MINUS} fill="orange" />} title={"Taxes"} />
  );
  const deductionsHeader = (
    <AppAccordionHeader leading={<AppIcon icon={MINUS} fill="orange" />} title={"Deductions"} />
  );

  const formatLocalDate = (date: LocalDate) => globalize().formatLocalDate(date, { date: "short" });
  const earningTitle = (earning: Earning) => {
    const chargeDate = earning.chargeDate
      ? formatLocalDate(earning.chargeDate)
      : translate(STRINGS.DATE_UNKNOWN);
    return (
      <Flex direction="column" gap={8}>
        <span>{`${chargeDate} • ${earning.earningDescription}`}</span>
        <span>{`🕐 ${earning.hours}h  💰$ ${earning.rate}/h`}</span>
      </Flex>
    );
  };
  const earningsList = (
    <AppList bordered>
      {earnings.map((earning, index) => (
        <PaystubAppListItem
          title={earningTitle(earning)}
          key={"earning-" + index}
          trailing={`$${formatNumberLocale(earning.amount)}`}
        />
      ))}
    </AppList>
  );

  const taxesList = (
    <AppList bordered>
      {taxes.map((tax, index) => (
        <PaystubAppListItem
          title={tax.taxDescription}
          key={"tax-" + index}
          trailing={`-$${formatNumberLocale(tax.taxAmount)}`}
        />
      ))}
    </AppList>
  );

  const deductionsList = (
    <AppList bordered>
      {deductions.map((deduction, index) => (
        <PaystubAppListItem
          title={deduction.deductionDescription}
          key={"deduction-" + index}
          trailing={`-$${formatNumberLocale(deduction.amount)}`}
        />
      ))}
    </AppList>
  );

  const accordionChildren: AccordionChild[] = [
    {
      header: earningsHeader,
      headerTrailing: `+ $${formatNumberLocale(totalGross)}`,
      items: earningsList,
    },
    {
      header: taxesHeader,
      headerTrailing: `- $${formatNumberLocale(totalTaxes)}`,
      items: taxesList,
    },
    {
      header: deductionsHeader,
      headerTrailing: `- $${formatNumberLocale(totalDeductions)}`,
      items: deductionsList,
    },
  ];

  return (
    <Scaffold
      title={
        <ScaffoldTitle
          label={`${translate(STRINGS.VOUCHER)} #${props.paystub.checkOrVoucherNumber}`}
        />
      }
      endActions={
        props.isLoading ? (
          <Spinner size={24} />
        ) : (
          <DownloadIcon fontSize={24} onClick={props.onClickDownload} />
        )
      }
      onClickGoBack={props.onClickGoBack}
    >
      <Margin m={16}>
        <AppCard>
          <AppCardContent>
            <PaystubSummaryCard paystub={props.paystub} />
            <AppAccordion>{accordionChildren}</AppAccordion>
          </AppCardContent>
        </AppCard>
      </Margin>
    </Scaffold>
  );
};

const PaystubAppListItem = styled(AppListItem)`
  .title {
    font-size: 14px;
  }
`;

export default PaystubDetailsScreen;
